import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  ArrowRightLeft,
  Clock,
  Loader2,
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';
import CompareResult from './timeline/CompareResult';
import TimelineEntry from './timeline/TimelineEntry';

const ExamTimeline = ({ patientId }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalExams, setTotalExams] = useState(0);
  const [totalAnalyses, setTotalAnalyses] = useState(0);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [selectedExams, setSelectedExams] = useState([]);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/neuro/patients/${patientId}/timeline`, { withCredentials: true });
        setTimeline(data.timeline || []);
        setTotalExams(data.total_exams);
        setTotalAnalyses(data.total_analyses);
      } catch (error) {
        logger.error('Error fetching timeline:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [patientId]);

  const handleSelectExam = (examId) => {
    setSelectedExams(prev => {
      if (prev.includes(examId)) return prev.filter(id => id !== examId);
      if (prev.length >= 2) return [prev[1], examId];
      return [...prev, examId];
    });
    setCompareResult(null);
  };

  const handleCompare = async () => {
    if (selectedExams.length !== 2) return;
    setComparing(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/neuro/compare-exams`, {
        exam_id_1: selectedExams[0], exam_id_2: selectedExams[1]
      }, { withCredentials: true });
      setCompareResult(data);
    } catch (error) {
      logger.error('Comparison error:', error);
    } finally {
      setComparing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <Card className="border-[#E5E4E0] bg-white">
        <CardContent className="p-8 text-center">
          <Clock className="w-10 h-10 text-[#5C5F5C] mx-auto mb-3" />
          <p className="text-[#5C5F5C]">No exam history yet</p>
          <p className="text-sm text-[#5C5F5C] mt-1">Complete neurological exams to see the timeline</p>
        </CardContent>
      </Card>
    );
  }

  const examEntries = timeline.filter(e => e.type === 'exam');

  return (
    <div className="space-y-6" data-testid="exam-timeline">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-[#E5E4E0] bg-white">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{totalExams}</p>
            <p className="text-xs text-[#5C5F5C]">Total Exams</p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E4E0] bg-white">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{totalAnalyses}</p>
            <p className="text-xs text-[#5C5F5C]">AI Analyses</p>
          </CardContent>
        </Card>
      </div>

      {/* Compare Selection */}
      {examEntries.length >= 2 && (
        <Card className="border-[#E5E4E0] bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-[#4B6651]" />
                <span className="text-sm font-medium text-[#2A2C2A]">
                  Compare Exams {selectedExams.length > 0 && `(${selectedExams.length}/2 selected)`}
                </span>
              </div>
              <Button
                size="sm"
                disabled={selectedExams.length !== 2 || comparing}
                onClick={handleCompare}
                className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
                data-testid="compare-exams-btn"
              >
                {comparing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
                Compare
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <CompareResult compareResult={compareResult} />

      {/* Timeline */}
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#E5E4E0]" />
        {timeline.map((entry, idx) => (
          <TimelineEntry
            key={`${entry.type}-${entry.id}`}
            entry={entry}
            idx={idx}
            examEntries={examEntries}
            selectedExams={selectedExams}
            expandedEntry={expandedEntry}
            setExpandedEntry={setExpandedEntry}
            onSelectExam={handleSelectExam}
          />
        ))}
      </div>
    </div>
  );
};

export default ExamTimeline;
